import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import MasterLocationService from "../../services/MasterLocationService";
import CompanyService from "../../services/companyService";
import StorageService from "../../services/StorageService";

export default function AddCompany() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [editMode, setEditMode] = useState(false);
  const [companyId, setCompanyId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({ mode: "onChange" });

  const country = watch("countryId");
  const state = watch("state");

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  // Load country list
  useEffect(() => {
    MasterLocationService.getAllCountries().then((data) =>
      setCountries(data || [])
    );
  }, []);

  // Load state list when country changes
  useEffect(() => {
    if (country) {
      // MasterLocationService.getStatesByCountry(country).then((data) => {
      //   setStates(data || []);
      //   if (!editMode) setValue("state", "");
      //   setCities([]);
      //   setValue("city", "");
      // });
      MasterLocationService.getStatesByCountry(country).then((data) => {
        setStates(data || []);

        if (!editMode) {
          setValue("state", "");
          setCities([]);
          setValue("city", "");
        }
      });

    } else {
      setStates([]);
      setCities([]);
      setValue("state", "");
      setValue("city", "");
    }
  }, [country, editMode, setValue]);

  // Load city list when state changes
  useEffect(() => {
    if (state) {
      // MasterLocationService.getCitiesByState(state).then((data) => {
      //   setCities(data || []);
      //   if (!editMode) setValue("city", "");
      // });
      MasterLocationService.getCitiesByState(state).then((data) => {
        setCities(data || []);

        if (!editMode) {
          setValue("city", "");
        }
      });
    } else {
      setCities([]);
      setValue("city", "");
    }
  }, [state, editMode, setValue]);

  // Load edit mode data if exists
  useEffect(() => {
    const loadCompany = async () => {
      if (!id) {
        setEditMode(false);
        return;
      }

      setEditMode(true);
      setCompanyId(id);
      setEditLoading(true);

      try {
        const record = await CompanyService.getById(id);
        if (!record) {
          showMessage("Unable to load company details", "error");
          return;
        }

        // Fill simple fields
        const simpleFields = { ...record };
        delete simpleFields.countryId;
        delete simpleFields.state;
        delete simpleFields.city;

        Object.entries(simpleFields).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            setValue(key, value);
          }
        });

        // Load Countries
        const countryData = await MasterLocationService.getAllCountries();
        setCountries(countryData);
        setValue("countryId", String(record.countryId));

        // Load States
        if (record.countryId) {
          const stateData = await MasterLocationService.getStatesByCountry(record.countryId);
          setStates(stateData);
          setValue("state", String(record.state));
        }

        // Load Cities
        if (record.state) {
          const cityData = await MasterLocationService.getCitiesByState(record.state);
          setCities(cityData);
          setValue("city", String(record.city));
        }

      } catch (error) {
        console.error("Error loading company details:", error);
        showMessage("Error loading company details", "error");
      }
      finally {
        setEditLoading(false);
      }
    };

    loadCompany();
  }, [id, setValue]);

  // Submit
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let res;
      if (editMode && companyId) {
        data.updatedBy = StorageService.loginData()?.employeeId || 1;
        res = await CompanyService.update(companyId, data);
        showMessage("Company updated successfully", "success");
      } else {
        data.createdBy = StorageService.loginData()?.employeeId || 1;
        res = await CompanyService.save(data);
        showMessage("Company added successfully", "success");
      }

      if (res?.status) {
        setTimeout(() => {
          localStorage.removeItem("editCompany");
          navigate("/cpnl/company/view", { state: { refresh: true } });
        }, 1000);
      } else if (res?.message) {
        showMessage(res.message, "error");
      } else {
        showMessage("Failed to save company", "error");
      }
    } catch (err) {
      console.error("Save error:", err);

      const backendMsg =
        err?.response?.data?.message ||   // ✅ backend message
        err?.message ||                  // fallback
        "Something went wrong";

      showMessage(backendMsg, "error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Company" : "Add Company"}
        secondTitle="Masters"
      />

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">

          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading company details...</div>
            </div>
          ) : (

            <form onSubmit={handleSubmit(onSubmit)}>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Company Code <span className="text-danger">*</span></label>
                  {/* <input type="text"
                    className="form-control"
                    {...register("companyCode", {
                      required: "Company Code is required",
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  /> */}

                  <input
                    type="text"
                    className="form-control"
                    {...register("companyCode", { required: "Company Code is required" })}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.preventDefault();
                        alert("Spaces are not allowed");
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.companyCode && <small className="text-danger">{errors.companyCode.message}</small>}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Company Name <span className="text-danger">*</span></label>
                  <input className="form-control" {...register("companyName", { required: "Company Name is required" })} />
                  {errors.companyName && <small className="text-danger">{errors.companyName.message}</small>}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Email </label>
                  <input type="email" className="form-control"
                    {...register("emailId", {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Enter a valid email address"
                      }
                    }

                    )} />
                  {errors.emailId && <small className="text-danger">{errors.emailId.message}</small>}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Mobile No <span className="text-danger">*</span></label>
                  <input className="form-control"
                    {...register("mobileNo", {
                      required: "Mobile No is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Enter valid 10 digit mobile number"
                      }
                    }
                    )} />
                  {errors.mobileNo && <small className="text-danger">{errors.mobileNo.message}</small>}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Phone</label>
                  <input className="form-control" {...register("phone")} />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Fax</label>
                  <input className="form-control" {...register("fax")} />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Address</label>
                  <input className="form-control" {...register("address")} />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Landmark</label>
                  <input className="form-control" {...register("landMark")} />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Website</label>
                  <input className="form-control" {...register("website")} />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Logo Small URL</label>
                  <input className="form-control" {...register("logoSmallURL")} />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Logo Big URL</label>
                  <input className="form-control" {...register("logoBigURL")} />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Country <span className="text-danger">*</span></label>
                  <select className="form-select" {...register("countryId", { required: "Country is required" })}>
                    <option value="">Select</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>{c.countryName}</option>
                    ))}
                  </select>
                  {errors.countryId && <small className="text-danger">{errors.countryId.message}</small>}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">State <span className="text-danger">*</span></label>
                  <select className="form-select" {...register("state", { required: "State is required" })}>
                    <option value="">Select</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>{s.stateName}</option>
                    ))}
                  </select>
                  {errors.state && <small className="text-danger">{errors.state.message}</small>}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">District <span className="text-danger">*</span></label>
                  <select className="form-select" {...register("city", { required: "District is required" })}>
                    <option value="">Select District</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>{c.cityName}</option>
                    ))}
                  </select>
                  {errors.city && <small className="text-danger">{errors.city.message}</small>}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Zip Code</label>
                  <input className="form-control" {...register("zip")} />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Establishment Year</label>
                  {/* <input type="number" className="form-control" {...register("estableshmentYear")} /> */}
                  <input
                    type="number"
                    className="form-control"
                    {...register("estableshmentYear", {
                      min: {
                        value: 0,
                        message: "Year cannot be negative"
                      }
                    })}
                  />

                  {errors.estableshmentYear && (
                    <small className="text-danger">{errors.estableshmentYear.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">PAN
                    <span className="text-danger">*</span>
                  </label>
                  <input className="form-control" {...register("PAN", {
                    required: "PAN is required",
                    pattern: {
                      value: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                      message: "PAN must be 10 characters (e.g. AAAAA9999A)",
                    }
                  })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.PAN && (
                    <small className="text-danger">{errors.PAN.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">TAN
                    <span className="text-danger">*</span>
                  </label>
                  <input className="form-control" {...register("TAN", {
                    required: "TAN is required",
                    pattern: {
                      value: /^[A-Z]{4}[0-9]{5}[A-Z]$/,
                      message: "TAN must be 10 characters (e.g. AAAA99999A)",
                    },
                  })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.TAN && (
                    <small className="text-danger">{errors.TAN.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    GSTN <span className="text-danger">*</span>
                    {/* <span className="text-danger">*</span> */}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("GSTN", {
                      required: "GSTN is required",
                      pattern: {
                        value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
                        message: "GSTN must be 15 characters",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.GSTN && (
                    <small className="text-danger">{errors.GSTN.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">CIN</label>
                  {/* <input
                    type="text"
                    className="form-control"
                    {...register("CIN", {
                      required: "CIN is required",
                      minLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                      maxLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  /> */}

                  <input
                    type="text"
                    className="form-control"
                    {...register("CIN", {
                      minLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                      maxLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.CIN && (
                    <small className="text-danger">{errors.CIN.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Trademark No</label>
                  <input className="form-control" {...register("trademarkNo")} />
                </div>
                <div className="mb-3 col-md-6"></div>
              </div>

              <button type="submit" className="btn btn-primary mt-2" disabled={loading}>
                <i className="ti ti-circle-check me-1"></i>
                {loading ? (editMode ? "Updating..." : "Saving...") : editMode ? "Update" : "Save"}
              </button>

              <button type="button" className="btn btn-secondary mt-2 ms-2" disabled={loading}
                onClick={() => {
                  navigate("/cpnl/company/view");
                }}
              >
                <i className="ti ti-cancel me-1"></i> Cancel
              </button>

            </form>
          )}
        </div>
      </div>
    </>
  );
}
